import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useApp } from 'contexts/app';
import CustomerService from 'services/customer';
import StoreSettingService from 'services/store_setting';
import LoyaltyPoinSettinService from 'services/loyalty_point';
import _ from 'lodash';

const service = new CustomerService();
const storeSettingService = new StoreSettingService();
const loyaltyService = new LoyaltyPoinSettinService();

export const useInfoModal = ({ id, open, onClose, onSave }) => {
  const navigate = useNavigate();
  const { showLoading, downloadPDF ,printPDF} = useApp();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customer, setCustomer] = useState({});
  const [orders, setOrders] = useState([]);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [loyaltyByOrder, setLoyaltyByOrder] = useState({});
  const [manualAdjustments, setManualAdjustments] = useState([]);
  const [showCustomerEdit, setShowCustomerEdit] = useState(false);
  const [showCustomerLoginEdit, setShowCustomerLoginEdit] = useState(false);
  const [isAccountLoginMode, setIsAccountLoginMode] = useState(false);

  // Sync modal open state with prop
  useEffect(() => {
    if (isModalOpen !== open) {
      setIsModalOpen(open);
    }
  }, [open]);

  // Load login method when modal opens
  useEffect(() => {
    if (open) {
      loadLoginMethod();
      loadLoyaltySetting();
    }
  }, [open]);

  // Load data when modal opens/closes
  useEffect(() => {
    if (!isModalOpen && open && onClose) {
      onClose();
    }
    if (isModalOpen) {
      loadData();
    }
  }, [isModalOpen]);

  const loadData = async () => {
    setCustomer({});
    setOrders([]);
    setLoyaltyByOrder({});
    setManualAdjustments([]);

    if (id) {
      try {
        const cus = await service.getByID(id);
        if (cus) {
          setCustomer(cus);
          const newOrder = _.orderBy(cus.orders, ['create_on'], ['desc']);
          setOrders(newOrder);

          const byOrder = {};
          const manuals = [];
          (cus.loyalty_transactions || []).forEach((tran) => {
            if (!tran) return;
            const point = Number(tran.point) || 0;
            if (tran.type === 'order' && tran.order_id) {
              if (!byOrder[tran.order_id]) byOrder[tran.order_id] = { earn: 0, use: 0 };
              byOrder[tran.order_id].earn += point;
            } else if (tran.type === 'order_minus' && tran.order_id) {
              if (!byOrder[tran.order_id]) byOrder[tran.order_id] = { earn: 0, use: 0 };
              byOrder[tran.order_id].use += Math.abs(point);
            } else if (tran.type === 'manual_add' || tran.type === 'manual_minus') {
              manuals.push({
                id: tran.id,
                kind: 'manual',
                type: tran.type,
                point,
                earn: tran.type === 'manual_add' ? point : 0,
                use: tran.type === 'manual_minus' ? Math.abs(point) : 0,
                reason: tran.reason || '',
                create_on: tran.create_on,
              });
            }
          });
          setLoyaltyByOrder(byOrder);
          setManualAdjustments(manuals);
        }
      } catch (error) {
        console.error('Error loading customer data:', error);
      }
    }
  };

  const loadLoginMethod = async () => {
    try {
      const setting = await storeSettingService.getWebsiteSetting();
      const loginMethod = String(setting?.login_method || '').toLowerCase();
      setIsAccountLoginMode(['account', 'both'].includes(loginMethod));
    } catch (error) {
      setIsAccountLoginMode(false);
    }
  };

  const loadLoyaltySetting = async () => {
    try {
      const setting = await loyaltyService.getLoyaltyPoint();
      setLoyaltyEnabled(!!setting?.on);
    } catch (error) {
      setLoyaltyEnabled(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onCustomerSave = async () => {
    await loadData();
    if (onSave) {
      onSave();
    }
  };

  return {
    isModalOpen,
    customer,
    orders,
    loyaltyEnabled,
    loyaltyByOrder,
    manualAdjustments,
    reload: loadData,
    showCustomerEdit,
    setShowCustomerEdit,
    showCustomerLoginEdit,
    setShowCustomerLoginEdit,
    isAccountLoginMode,
    handleCancel,
    onCustomerSave,
    navigate,
    showLoading,
    downloadPDF,
  };
};
